import React, { useEffect, useState } from 'react';
import CrudTable from '../../components/common/CrudTable';
import { transactionAPI, studentAPI, trainerAPI, gymAPI, groupAPI } from '../../api/services';
import { formatCurrency } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';

const TransactionsAdmin = () => {
  const [students, setStudents] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [groups, setGroups] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    // Load related entities for dropdowns
    studentAPI.getAll()
      .then(res => setStudents(res.data?.data || []))
      .catch(() => {});
    
    trainerAPI.getAll()
      .then(res => setTrainers(res.data?.data || []))
      .catch(() => {});
      
    gymAPI.getAll()
      .then(res => setBranches(res.data?.data || []))
      .catch(() => {});
      
    groupAPI.getAll()
      .then(res => setGroups(res.data?.data || []))
      .catch(() => {});
  }, []);

  const COLUMNS = [
    { key: 'transactionType', label: t('fields.type') },
    { key: 'amount', label: t('fields.amount'), render: (item) => formatCurrency(item.amount) },
    { key: 'month', label: t('fields.month'), render: (item) => t(`months.${item.month}`) },
    { key: 'studentDto', label: t('fields.student'), render: (item) => item.studentDto?.fullName || '—' },
    { key: 'trainerDto', label: t('fields.trainer'), render: (item) => item.trainerDto?.fullName || '—' },
    { key: 'branchDto', label: t('fields.branch'), render: (item) => item.branchDto?.name || '—' },
    { key: 'comment', label: t('fields.comment') },
  ];

  const FORM_FIELDS = (form) => [
    { name: 'amount', label: t('fields.amount'), type: 'number', placeholder: t('fields.amount') },
    { 
      name: 'month', 
      label: t('fields.month'), 
      type: 'select',
      options: [
        { label: t('months.JANUARY'), value: 'JANUARY' },
        { label: t('months.FEBRUARY'), value: 'FEBRUARY' },
        { label: t('months.MARCH'), value: 'MARCH' },
        { label: t('months.APRIL'), value: 'APRIL' },
        { label: t('months.MAY'), value: 'MAY' },
        { label: t('months.JUNE'), value: 'JUNE' },
        { label: t('months.JULY'), value: 'JULY' },
        { label: t('months.AUGUST'), value: 'AUGUST' },
        { label: t('months.SEPTEMBER'), value: 'SEPTEMBER' },
        { label: t('months.OCTOBER'), value: 'OCTOBER' },
        { label: t('months.NOVEMBER'), value: 'NOVEMBER' },
        { label: t('months.DECEMBER'), value: 'DECEMBER' }
      ]
    },
    { 
      name: 'branchId', 
      label: t('fields.branch'), 
      type: 'select',
      options: branches.map(b => ({ label: b.name, value: b.id }))
    },
    {
      name: 'groupId',
      label: t('fields.group'),
      type: 'select',
      options: groups
        .filter(g => g.gymDto?.id == form.branchId)
        .map(g => ({ label: g.name, value: g.id }))
    },
    { 
      name: 'studentId', 
      label: t('fields.student'), 
      type: 'select',
      disabled: !!form.trainerId,
      options: students
        .filter(s => s.groupDto?.id == form.groupId)
        .map(s => ({ label: s.fullName, value: s.id }))
    },
    { 
      name: 'trainerId', 
      label: t('fields.trainer'), 
      type: 'select',
      disabled: !!form.studentId,
      options: trainers.map(t => ({ label: t.fullName, value: t.id }))
    },
    { name: 'comment', label: t('fields.comment'), type: 'textarea', placeholder: t('fields.comment') },
  ];

  return (
    <CrudTable
      title={t('admin.transactions')}
      entityName={t('fields.transaction')}
      columns={COLUMNS}
      formFields={FORM_FIELDS}
      fetchData={async () => {
        const res = await transactionAPI.getAll();
        const data = res.data?.data || [];
        // Map DTOs to IDs for the edit form
        return data.map(item => ({
          ...item,
          studentId: item.studentDto?.id || '',
          groupId: item.studentDto?.groupDto?.id || '',
          trainerId: item.trainerDto?.id || '',
          branchId: item.branchDto?.id || ''
        }));
      }}
      onAdd={(data) => {
        const payload = { ...data };
        delete payload.groupId;
        return transactionAPI.add(payload);
      }}
      onUpdate={(id, data) => {
        const payload = { ...data };
        delete payload.groupId;
        return transactionAPI.update(id, payload);
      }}
    />
  );
};

export default TransactionsAdmin;
